/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { EpisodeVodItem, FolderVodItemKernel, VodItem } from 'client-code/vod-browse';

import { Locale, LocaleKeys } from '../../../../core/locale';
import { RootState, useTivoSelector } from '../../../../core/tivo/tivo-redux-wrapper';
import { CcbSwitch } from '../../../../core/utils/ccb';
import { CcbSwitchName } from '../../../../core/utils/ccb/enums';
import { BWHListType } from '../../../../store/biaxialwidget/biaxialwidget.props';
import { VodPreview } from '../../VodPreview/VodPreview';
import { VodScreenType } from '../../VodPreview/VodPreviewProps';
import { ContentTile, EmptyTile, ViewAllTile } from '../ContentTile';

import './BWHorizontalList.scss';

const BWHorizontalList: FunctionComponent = (): JSX.Element => {
    const viewAllTileRef = useRef<HTMLDivElement>(null);
    const horizontalContainerRef = useRef<HTMLDivElement>(null);
    const isVodPreviewEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
        CcbSwitchName.ENABLE_VOD_PREVIEW_REMOVE_IN_KST_14762,
    );

    const [horizontalList, setHorizontalList] = useState<JSX.Element[]>([]);
    const [screenType, setScreenType] = useState<string>('');
    const [isViewAllVisible, setIsViewAllVisible] = useState<boolean>(true);

    const { horizontalItem, horizontalPosition, isTurboScroll } = useTivoSelector(
        (state: RootState) => state.biaxialWidget,
    );
    const { listItems = [], listType, isEmpty } = horizontalItem;

    useEffect(() => {
        setIsViewAllVisible(isElementInHorizontalViewport(viewAllTileRef.current));
    }, [horizontalPosition, horizontalList]);

    useEffect(() => {
        if (listType === BWHListType.VOD_BROWSE_MAIN) {
            const lastItem = listItems?.[listItems.length - 1] as VodItem;
            setScreenType((lastItem?.vodItemKernel as FolderVodItemKernel)?.screenType || '');
            populateVodBrowseMainHorizontalList(listItems as Array<VodItem>);
        } else {
            const emptyItem: JSX.Element[] = [];
            emptyItem.push(
                <div className="horizontal-item" key="empty-tile">
                    <EmptyTile isEmpty={isEmpty} />
                </div>,
            );
            setHorizontalList(emptyItem);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listItems, listType]);

    const populateVodBrowseMainHorizontalList = useCallback(
        (items: Array<VodItem>) => {
            const horizontalItems: JSX.Element[] = items.map((item, idx) => (
                <div key={`${item?.title}-${idx}`} className="horizontal-item">
                    {items.length - 1 !== idx ? (
                        <ContentTile
                            type={item?.vodItemType?.toLowerCase()}
                            title={item?.title}
                            digObjectId={item?.digObjectId}
                            isAdult={item?.vodItemKernel?.isAdult}
                            percentWatched={(item as EpisodeVodItem)?.percentWatched}
                            videoResolution={(item as EpisodeVodItem)?.videoResolution}
                            expiryStatus={(item as EpisodeVodItem)?.expiryStatus}
                            seasonNumber={(item as EpisodeVodItem)?.seasonNumber}
                            episodeNumber={(item as EpisodeVodItem)?.episodeNumber}
                        />
                    ) : (
                        <ViewAllTile
                            screenType={(item?.vodItemKernel as FolderVodItemKernel)?.screenType}
                            ref={viewAllTileRef}
                        />
                    )}
                </div>
            ));

            if (horizontalItems.length === 0) {
                horizontalItems.push(
                    <div className="horizontal-item" key="empty-tile">
                        <EmptyTile isEmpty={isEmpty} />
                    </div>,
                );
            }
            setHorizontalList(horizontalItems);
        },
        [isEmpty],
    );

    const isElementInHorizontalViewport = (element: HTMLDivElement | null) => {
        if (!element) return false;

        const rect = element.getBoundingClientRect();

        // Check if in browser viewport
        const inViewport =
            (rect.left >= 0 && rect.left <= window.innerWidth) ||
            (rect.right >= 0 && rect.right <= window.innerWidth);

        return inViewport;
    };

    // memoize derived indices and slices to avoid re-computation each render
    const leftSectionIndex = useMemo(
        () =>
            horizontalList.length > 0
                ? (horizontalPosition - 1 + horizontalList.length) % horizontalList.length
                : 0,
        [horizontalPosition, horizontalList.length],
    );

    const rightSectionIndex = useMemo(
        () => (horizontalList.length > 0 ? (horizontalPosition + 1) % horizontalList.length : 0),
        [horizontalPosition, horizontalList.length],
    );

    const rightHorizontalList: JSX.Element[] = useMemo(() => {
        const result: JSX.Element[] = [];
        if (horizontalList.length === 0) return result;

        if (rightSectionIndex > leftSectionIndex) {
            result.push(...horizontalList.slice(rightSectionIndex, horizontalList.length));
            result.push(...horizontalList.slice(0, leftSectionIndex));
        } else if (rightSectionIndex < leftSectionIndex) {
            result.push(...horizontalList.slice(rightSectionIndex, leftSectionIndex));
        }
        return result;
    }, [horizontalList, leftSectionIndex, rightSectionIndex]);

    const leftOrRightIndicatorClass = useMemo(
        () =>
            horizontalPosition > (horizontalList.length - 1) / 2
                ? 'right-indicator'
                : 'left-indicator',
        [horizontalPosition, horizontalList.length],
    );

    const horizontalItemDiv = useMemo(
        () => (
            <div className="horizontal-item-container" ref={horizontalContainerRef}>
                <div
                    className={`view-all-indicator ${leftOrRightIndicatorClass}${isViewAllVisible ? ' hidden' : ''}`}
                >
                    {Locale.getString(LocaleKeys.VIEW_ALL_TEXT).toLowerCase()}
                </div>
                <div className="horizontal-item-list">
                    <div className="left-section">
                        {horizontalList.length > 1 && horizontalList[leftSectionIndex]}
                    </div>
                    <div className="middle-section">
                        {listItems.length > 0 &&
                        horizontalList.length > 1 &&
                        horizontalPosition === horizontalList.length - 1 ? (
                            <ViewAllTile
                                screenType={screenType}
                                isSelected={true}
                                ref={viewAllTileRef}
                            />
                        ) : (
                            horizontalList[horizontalPosition]
                        )}
                    </div>
                    <div className="right-section">{rightHorizontalList.slice(0, 6)}</div>
                    {isEmpty && (
                        <div className="empty-tile-message">
                            {Locale.getString(LocaleKeys.EMPTY_TILE_MESSAGE_VOD_MAIN_SCREEN)}
                        </div>
                    )}
                </div>
            </div>
        ),
        [
            horizontalList,
            leftSectionIndex,
            rightHorizontalList,
            horizontalPosition,
            leftOrRightIndicatorClass,
            isViewAllVisible,
            screenType,
            listItems.length,
            isEmpty,
        ],
    );

    return (
        <React.Fragment>
            {horizontalList.length > 0 && horizontalItemDiv}
            <div className="vod-preview-container">
                {isVodPreviewEnabled && !isTurboScroll && (
                    <VodPreview
                        screenType={VodScreenType.VOD_MAIN_SCREEN}
                        item={listItems[horizontalPosition]}
                    />
                )}
            </div>
        </React.Fragment>
    );
};

export default BWHorizontalList;
